import StatisticsImage from "../../Cloudinary/StatisticsImage";
import { BoosterChips } from "../../BoosterChips";
import { boosterIdKey } from "../../../Common/constants";
import { getBoosterCategory } from "../../../Common/utils";

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

/**
 * Renders a booster chip icon based on boosterId
 * @param {number} boosterId - The booster ID to display
 */
const BoosterChipIcon = ({
  boosterId,
  hasMultipleChips = false,
  index = 0,
}) => {
  if (!boosterId || boosterId <= 0) return null;

  return (
    <div
      className={`si-h2h-player-card__player-chip si-h2h-player-card__player-chip--${index} ${
        hasMultipleChips ? "si-h2h-player-card__player-chip--multiple" : ""
      }`}
    >
      {boosterId === "finalFix" && (
        <BoosterChips
          boosterCls={getBoosterCategory(boosterId)?.boosterCls}
          boosterIcon={getBoosterCategory(boosterId)?.boosterIcon}
        />
      )}
      {boosterId === "extraDRS" && (
        <BoosterChips
          boosterCls={getBoosterCategory(boosterId)?.boosterCls}
          boosterIcon={getBoosterCategory(boosterId)?.boosterIcon}
        />
      )}
      {boosterId === "replaced" && (
        <div className="si-booster__box  si-not-playing">
          <div className="si-booster__box-icon">
            <i className="f1i-not-playing"></i>
          </div>
        </div>
      )}
      {boosterId === "captain" && (
        <div className="si-booster__box  si-captain">
          <div className="si-booster__box-icon">
            <span>2X</span>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Renders the captain badge icon
 */
const CaptainBadge = () => (
  <div className="si-h2h-player-card__player-chip-captain">
    <i className="f1i-captain"></i>
  </div>
);

/**
 * Renders a player's image with optional booster chip or captain badge
 *
 * @param {Object} props
 * @param {Object} props.player - Player data object
 * @param {number} props.boosterId - Booster ID to show on this player (optional)
 * @param {boolean} props.showCaptain - Whether to show captain badge
 */
const PlayerImage = ({ player, boosterId = [] }) => {
  const hasMultipleChips = boosterId?.length > 1;

  return (
    <div className="si-h2h-player-card__player-item">
      <div className="si-h2h-player-card__image">
        <StatisticsImage statisticsPlayer={player} />
      </div>
      {boosterId?.length > 0 &&
        boosterId?.map((id, index) => (
          <BoosterChipIcon
            key={id}
            boosterId={id}
            hasMultipleChips={hasMultipleChips}
            index={index}
          />
        ))}
    </div>
  );
};

/**
 * Renders the score display
 * @param {string|number} score - Score value to display
 */
const ScoreDisplay = ({ score }) => (
  <div className="si-h2h-player-card__score">
    <span>{score}</span>
  </div>
);

/**
 * Renders the player name based on size variant
 *
 * @param {Object} props
 * @param {string} props.size - "sm" for short name, "lg" for full name
 * @param {string} props.shortName - Short display name (e.g., "VER")
 * @param {string} props.firstName - First name for large size
 * @param {string} props.lastName - Last name for large size
 * @param {boolean} props.showOnlyLastName - If true, only show lastName for lg size (useful for constructors)
 */
const PlayerNameDisplay = ({
  size,
  shortName,
  firstName,
  lastName,
  showOnlyLastName = false,
}) => {
  const nameContent =
    size === "lg" ? (
      showOnlyLastName ? (
        <span className="si-h2h-player-card__display-name-last">
          {lastName}
        </span>
      ) : (
        <>
          <span className="si-h2h-player-card__display-name-first">
            {firstName}
          </span>
          <span className="si-h2h-player-card__display-name-last">
            {lastName}
          </span>
        </>
      )
    ) : (
      <span className="si-h2h-player-card__display-name-short">
        {shortName}
      </span>
    );

  return <div className="si-h2h-player-card__display-name">{nameContent}</div>;
};

/**
 * Renders a booster chip below stacked players or at the end of multi-player rows
 * @param {number} boosterId - Booster ID
 * @param {boolean} isCaptain - Whether to show captain badge instead
 */
// const StackBooster = ({ boosterId, isCaptain }) => {
//   if (!boosterId && !isCaptain) return null;

//   return (
//     <div className="si-h2h-player-card__stack-booster">
//       {isCaptain ? <CaptainBadge /> : <BoosterChipIcon boosterId={boosterId} />}
//     </div>
//   );
// };

// ============================================================================
// SINGLE PLAYER VIEW
// ============================================================================

/**
 * Renders a single player card layout
 * Layout varies based on cardType:
 * - current: Score | Name | Image
 * - opponent: Image | Name | Score
 */
const SinglePlayerView = ({
  player,
  score,
  size,
  shortName,
  firstName,
  lastName,
  boosterId,
  isCaptain,
  isOpponent,
  showOnlyLastName = false,
}) => {
  // Handle score when it's passed as an array (extract first element)
  const displayScore = Array.isArray(score) ? score[0] : score;
  const isReplaced = Array.isArray(boosterId) && boosterId.includes("replaced");

  const imageWithStack = (
    <div className="si-h2h-player-card__players-stack">
      <PlayerImage player={player} boosterId={boosterId} />
    </div>
  );

  const nameDisplay = (
    <PlayerNameDisplay
      size={size}
      shortName={shortName}
      firstName={firstName}
      lastName={lastName}
      showOnlyLastName={showOnlyLastName}
    />
  );

  const scoreDisplay = <ScoreDisplay score={displayScore} />;

  if (isOpponent) {
    // Opponent layout: Image | Name | Score
    return (
      <>
        <div
          className={`si-h2h-player-card__left ${isReplaced ? "si-h2h-player-card__replaced" : ""}`}
        >
          {imageWithStack}
          {nameDisplay}
        </div>
        <div
          className={`si-h2h-player-card__right ${isReplaced ? "si-h2h-player-card__replaced" : ""}`}
        >
          {scoreDisplay}
        </div>
      </>
    );
  }

  // Current user layout: Score | Name | Image
  return (
    <>
      <div
        className={`si-h2h-player-card__left ${isReplaced ? "si-h2h-player-card__replaced" : ""}`}
      >
        {scoreDisplay}
      </div>
      <div
        className={`si-h2h-player-card__right ${isReplaced ? "si-h2h-player-card__replaced" : ""}`}
      >
        {nameDisplay}
        {imageWithStack}
      </div>
    </>
  );
};

// ============================================================================
// MULTI-PLAYER ROW VIEW
// ============================================================================

/**
 * Renders a single row within the multi-player view
 * Each player gets their own row with image, name, and score
 */
const MultiPlayerRow = ({
  player,
  index,
  boosterId,
  size,
  isOpponent,
  playerScore,
}) => {
  const playerBoosterId = boosterId || [];
  const isStackedRow = index > 0;
  const isReplaced = Array.isArray(boosterId) && boosterId.includes("replaced");

  // Extract player name based on size
  const playerName =
    size === "lg" ? (
      <>
        <span className="si-h2h-player-card__display-name-first">
          {player?.FirstName}
        </span>
        <span className="si-h2h-player-card__display-name-last">
          {player?.LastName}
        </span>
      </>
    ) : (
      <span className="si-h2h-player-card__display-name-short">
        {player?.DriverTLA}
      </span>
    );

  // Use passed playerScore, fallback to player?.OverallPpints
  const displayScore = playerScore ?? player?.OverallPpints;

  const rowClasses = `si-h2h-player-card__row${
    isStackedRow ? " si-h2h-player-card__row--stacked" : ""
  }`;

  const imageContent = (
    <PlayerImage player={player} boosterId={playerBoosterId} />
  );

  const nameContent = (
    <div className="si-h2h-player-card__display-name">{playerName}</div>
  );

  const scoreContent = <ScoreDisplay score={displayScore} />;

  if (isOpponent) {
    // Opponent row: Image | Name | Score
    return (
      <div key={`player-row-${index}`} className={rowClasses}>
        <div
          className={`si-h2h-player-card__left ${isReplaced ? "si-h2h-player-card__replaced" : ""}`}
        >
          {imageContent}
          {nameContent}
        </div>
        <div
          className={`si-h2h-player-card__right ${isReplaced ? "si-h2h-player-card__replaced" : ""}`}
        >
          {scoreContent}
        </div>
      </div>
    );
  }

  // Current user row: Score | Name | Image
  return (
    <div key={`player-row-${index}`} className={rowClasses}>
      <div
        className={`si-h2h-player-card__left ${isReplaced ? "si-h2h-player-card__replaced" : ""}`}
      >
        {scoreContent}
      </div>
      <div
        className={`si-h2h-player-card__right ${isReplaced ? "si-h2h-player-card__replaced" : ""}`}
      >
        {nameContent}
        {imageContent}
      </div>
    </div>
  );
};

/**
 * Renders multiple players as separate rows
 * Used when players.length > 1 (e.g., replaced player scenarios)
 */
const MultiPlayerView = ({
  players,
  playerChips,
  boosterId,
  size,
  isOpponent,
  score,
}) => {
  // score can be an array of scores corresponding to each player
  const scoreArray = Array.isArray(score) ? score : [];

  return (
    <div className="si-h2h-player-card__rows">
      {players.map((player, index) => {
        const boostersWithoutReplaced = boosterId.filter(
          (booster) => booster !== "replaced"
        );
        const replacedBooster = boosterId.filter(
          (booster) => booster === "replaced"
        );
        const filteredBoosters =
          index === players.length - 1
            ? boostersWithoutReplaced
            : replacedBooster;

        return (
          <MultiPlayerRow
            key={`player-row-${index}`}
            player={player}
            index={index}
            boosterId={filteredBoosters}
            size={size}
            isOpponent={isOpponent}
            playerScore={scoreArray[index]}
          />
        );
      })}
    </div>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

/**
 * HeadToHeadPlayerCard Component
 *
 * Displays a player card for head-to-head comparisons with various configurations:
 * - Base view: Score, display name, and player image
 * - With booster chip: Base view with a chip at the bottom-right of the image
 * - Replaced player view: Multiple players as separate rows with individual name/score
 *
 * The card scales responsively based on its container width.
 *
 * @param {Object} props
 * @param {string} props.cardType - "current" | "opponent" - determines layout direction
 * @param {Array} props.players - Array of player objects to display (can be 1, 2, or 3 players)
 * @param {string|number} props.score - Score to display for single player (e.g., "000" or 123)
 * @param {string} props.shortName - Short display name for single player (e.g., "VER", "RBR")
 * @param {string} props.fullName - Full display name for single player (e.g., "First Last")
 * @param {string} props.firstName - First name for single player (e.g., "Lewis")
 * @param {string} props.lastName - Last name for single player (e.g., "Hamilton")
 * @param {string} props.size - "sm" | "lg" - determines which name to display (default: "sm")
 * @param {number} props.boosterId - Booster ID to display chip at bottom-right of image (optional)
 * @param {Array} props.playerChips - Array of chip configs for each player [{boosterId: 1}, ...] (optional)
 * @param {Array} props.playerData - Per-player data for multi-player view [{shortName, fullName, score}, ...] (optional)
 * @param {boolean} props.isCaptain - Whether to show captain badge
 * @param {boolean} props.showOnlyLastName - If true, only show lastName for lg size (useful for constructors)
 */
const HeadToHeadPlayerCard = ({
  cardType = "current",
  players = [],
  score = "000",
  shortName = "",
  fullName = "",
  firstName = "",
  lastName = "",
  size = "sm",
  boosterId = null,
  playerChips = [],
  playerData = [],
  isCaptain = false,
  showOnlyLastName = false,
}) => {
  const isOpponent = cardType === "opponent";
  const hasMultiplePlayers = players.length > 1;

  // Build CSS classes for the card container
  const cardClasses = [
    "si-h2h-player-card",
    `si-h2h-player-card--${cardType}`,
    `si-h2h-player-card--${size}`,
    hasMultiplePlayers ? "si-h2h-player-card--stacked" : "",
  ]
    .filter(Boolean)
    .join(" ");
  // return null;

  // Render content based on number of players
  const renderContent = () => {
    if (hasMultiplePlayers) {
      return (
        <MultiPlayerView
          players={players}
          boosterId={boosterId}
          size={size}
          isOpponent={isOpponent}
          score={score}
        />
      );
    }

    // Single player view
    return (
      <SinglePlayerView
        player={players[0]}
        score={score}
        size={size}
        shortName={shortName}
        firstName={firstName}
        lastName={lastName}
        boosterId={boosterId}
        isCaptain={isCaptain}
        isOpponent={isOpponent}
        showOnlyLastName={showOnlyLastName}
      />
    );
  };

  return <div className={cardClasses}>{renderContent()}</div>;
};

export default HeadToHeadPlayerCard;
